import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { stopChromeAutofill } from './stop-chrome-autofill';
import { CrmModelDrivenFormErrorComponent } from './model-driven-form-error/model-driven-form-error.component';
import { FieldValidation } from '../field-validation';
import { FieldWarningService } from './model-driven-form-error/field-warning.service';
import { InlineEditService } from '../inline-edit.service';
import { FieldValueInterface, ObjectType, RenderableFieldInterface } from '../../../tokens-and-interfaces';

export class IntegerFieldHandler implements RenderableFieldInterface {
  formControl: UntypedFormControl;

  constructor(
    public objectType: ObjectType,
    public fieldId: string,
    public name: string,
    public label: string,
    public value: number,
    public description: string,
    public readOnly = false,
    public validation: FieldValidation = new FieldValidation(),
  ) {
    this.formControl = new UntypedFormControl(value);
  }

  setValidation(validation: FieldValidation): void {
    this.validation = validation;
    this.formControl.setValidators(validation.getValidators());
    this.formControl.setAsyncValidators(validation.getAsyncValidators());
  }

  render(viewContainerRef: ViewContainerRef): void {
    viewContainerRef.clear();
    const component = viewContainerRef.createComponent<IntegerRendererComponent>(IntegerRendererComponent);
    component.instance.fieldId = this.fieldId;
    component.instance.objectType = this.objectType;
    component.instance.control = this.formControl;
    component.instance.label = this.label;
    component.instance.validation = this.validation;
    component.instance.readOnly = this.readOnly;
  }

  hasModification(): boolean {
    return this.formControl?.dirty;
  }

  fieldValue(): FieldValueInterface {
    return {
      fieldId: this.fieldId,
      integerValue: this.formControl.value,
    };
  }
}

@Component({
  template: `
    <div class="crm-read-only-container" *ngIf="readOnly; else formField">
      <div class="crm-field-label">
        {{ label | translate }}
      </div>
      <div class="crm-field-value">
        <!-- ng-container necessary since Angular interpolation will add empty space -->
        <ng-container *ngIf="control.value">
          {{ control.value }}
        </ng-container>
      </div>
    </div>
    <ng-template #formField>
      <glxy-form-field>
        <glxy-label *ngIf="label">{{ label | translate }}</glxy-label>
        <input
          matInput
          [formControl]="control"
          [placeholder]="'MODEL_DRIVEN_FORM.ADD_FIELD_VALUE' | translate: { fieldName: label?.toLowerCase() }"
          type="number"
          inputmode="numeric"
          pattern="[0-9-]*"
          digitOnly
          [allowNegatives]="true"
          autocomplete="{{ disableAutoComplete }}"
          (blur)="saveField()"
        />

        <crm-model-driven-form-error
          [objectType]="objectType"
          [fieldId]="fieldId"
          [label]="label"
          [control]="control"
          [validation]="validation"
        ></crm-model-driven-form-error>
      </glxy-form-field>
    </ng-template>
  `,
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    DigitOnlyModule,
    TranslateModule,
    GalaxyFormFieldModule,
    CrmModelDrivenFormErrorComponent,
  ],
  providers: [FieldWarningService],
  styleUrls: ['./renderers.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegerRendererComponent {
  fieldId!: string;
  objectType!: ObjectType;
  control!: UntypedFormControl;
  label!: string;
  validation!: FieldValidation;
  readOnly!: boolean;

  disableAutoComplete: string = stopChromeAutofill();

  constructor(private readonly inlineEditService: InlineEditService) {}

  saveField(): void {
    this.inlineEditService.saveInlineEdit(this.objectType, {
      fieldId: this.fieldId,
      integerValue: this.control.value,
    });
  }
}
